import * as htmlToImage from "html-to-image";
import { CalendarDays, Copy, Download, Link, MapPin } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import Spinner from "../../components/Spinner";

const [HEIGHT, WIDTH] = [1350, 1080]; // Default dimensions for the image

function GenerateSingleJobImage({
	companyName,
	jobTitle,
	jobDescription,
	qualification,
	jobLink,
	deadline,
	jobLocation,
}) {
	const imageRef = React.useRef(null);
	const [isDownloading, setIsDownloading] = React.useState(false);
	const [isCopying, setIsCopying] = React.useState(false);

	const downloadImage = async () => {
		if (imageRef.current && !isDownloading) {
			setIsDownloading(true);
			try {
				const dataUrl = await htmlToImage.toPng(imageRef.current, {
					quality: 3,
					pixelRatio: 10,
				});
				const link = document.createElement("a");
				link.download = `${companyName}-job-openings.png`;
				link.href = dataUrl;
				link.click();
			} catch (error) {
				toast.error("Failed to download image" + error);
			} finally {
				setIsDownloading(false);
			}
		}
	};

	const copyToClipboard = async () => {
		if (imageRef.current && !isCopying) {
			setIsCopying(true);
			try {
				const blob = await htmlToImage.toBlob(imageRef.current, {
					quality: 3,
					pixelRatio: 10,
				});
				await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
				toast.success("Image copied to clipboard!");
			} catch (error) {
				toast.error("Failed to copy image to clipboard");
			} finally {
				setIsCopying(false);
			}
		}
	};
	return (
		<div className="p-6 flex flex-col items-center justify-center">
			<div
				className="shadow-lg bg-primary-800 p-12"
				style={{
					width: WIDTH / 2,
					height: HEIGHT / 2,
				}}
				ref={imageRef}
			>
				<div className="bg-white flex items-center h-full justify-center flex-col px-4">
					<img src="/images/logo.png" className="w-24 mb-2" />

					<h2 className="text-2xl font-plus-jakarta" contentEditable>
						{companyName}
					</h2>
					<h1 className="text-4xl font-plus-jakarta">is Hiring</h1>

					<div className="mt-4 border-2 w-10/12 border-primary-800 rounded-full py-3">
						<h3 className="text-xl text-center font-semibold px-6" contentEditable>
							{jobTitle}
						</h3>
					</div>

					<div className="w-10/12 mt-6">
						<p className="text-center text-sm leading-tight">{jobDescription}</p>
					</div>

					<div className="my-4 w-10/12 text-white bg-primary-800 rounded-full py-1 px-6">
						<p className="text-center font-semibold text-sm" contentEditable>
							{qualification}
						</p>
					</div>

					<div className="grid grid-cols-2 w-10/12 gap-4 mt-4">
						<div className="flex items-center gap-2">
							<div className=" text-white bg-primary-800 rounded-full p-2">
								<CalendarDays size={12} />
							</div>
							<div className="font-medium text-sm leading-none">{deadline}</div>
						</div>
						<div className="flex items-center gap-2">
							<div className=" text-white bg-primary-800 rounded-full p-2">
								<MapPin size={12} />
							</div>
							<div className="font-medium text-sm leading-none">{jobLocation}</div>
						</div>
						<div className="flex items-center gap-2 col-span-2">
							<div className=" text-white bg-primary-800 rounded-full p-2">
								<Link size={12} />
							</div>
							<div className="font-medium text-sm leading-none">{jobLink}</div>
						</div>
					</div>
				</div>
			</div>
			{/* Action Buttons */}
			<div className="flex justify-center gap-4 mt-4">
				<button
					onClick={downloadImage}
					disabled={isDownloading || isCopying}
					className="flex items-center gap-2 bg-primary-800 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isDownloading ? <Spinner /> : <Download size={20} />}
					{isDownloading ? "Downloading..." : "Download Image"}
				</button>
				<button
					onClick={copyToClipboard}
					disabled={isDownloading || isCopying}
					className="flex items-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isCopying ? <Spinner /> : <Copy size={20} />}
					{isCopying ? "Copying..." : "Copy to Clipboard"}
				</button>
			</div>
		</div>
	);
}

export default GenerateSingleJobImage;
